import React, { useMemo, useState } from 'react';
import { BarChart3Icon, CheckCircle2Icon, ChevronDownIcon, ChevronRightIcon, ImageIcon, MailIcon, MegaphoneIcon, MessagesSquareIcon, PaletteIcon, SearchIcon, XIcon } from 'lucide-react';
import { CarliStars } from './CarliStars';
import { siFacebook, siInstagram, siLinkedin, siPinterest, siTiktok, siYoutube } from 'simple-icons/icons';
import { owners } from './RecoveryPortal';
type PlatformType = 'ads' | 'social' | 'lifecycle' | 'creative';
type Platform = {
  name: string;
  type: PlatformType;
  iconPath?: string;
  iconUrl?: string;
  maskUrl?: string;
  connected: boolean;
  spend?: string;
  creative?: string;
  role?: string;
  capabilities?: string[];
  library?: string;
};
type Campaign = {
  name: string;
  brand: string;
  platform: string;
  window: string;
  spend: string;
  revenue: string;
  roas: string;
  units: string;
  status: 'Active' | 'Scheduled' | 'Completed';
};
const initialPlatforms: Platform[] = [{
  name: 'Google Ads',
  type: 'ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/7Rcmt8YbLjZHpSjzxgNXp2/google.svg',
  connected: true,
  spend: '$4,280',
  creative: '4 active assets'
}, {
  name: 'Facebook Ads',
  type: 'ads',
  iconPath: siFacebook.path,
  connected: true,
  spend: '$3,840',
  creative: '6 active assets'
}, {
  name: 'Instagram Ads',
  type: 'ads',
  iconPath: siInstagram.path,
  connected: true,
  spend: '$2,960',
  creative: '5 active assets'
}, {
  name: 'Vibe.co',
  type: 'ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/4QsPQBQikeUopo55GPTpMC/vibe.svg',
  connected: false
}, {
  name: 'MNTN',
  type: 'ads',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/81pvkRmDnW9HU5AzT3w7th/mntn.svg',
  connected: false
}, {
  name: 'Pinterest Ads',
  type: 'ads',
  iconPath: siPinterest.path,
  connected: false
}, {
  name: 'TikTok Ads',
  type: 'ads',
  iconPath: siTiktok.path,
  connected: false
}, {
  name: 'Facebook',
  type: 'social',
  iconPath: siFacebook.path,
  connected: true,
  creative: '12 posts this month'
}, {
  name: 'Instagram',
  type: 'social',
  iconPath: siInstagram.path,
  connected: true,
  creative: '18 posts this month'
}, {
  name: 'YouTube',
  type: 'social',
  iconPath: siYoutube.path,
  connected: false
}, {
  name: 'Pinterest',
  type: 'social',
  iconPath: siPinterest.path,
  connected: false
}, {
  name: 'LinkedIn',
  type: 'social',
  iconPath: siLinkedin.path,
  connected: false
}, {
  name: 'TikTok',
  type: 'social',
  iconPath: siTiktok.path,
  connected: false
}, {
  name: 'Klaviyo',
  type: 'lifecycle',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/3rX7mUrbpBP38ioN5pJ69R/Klaviyo.svg',
  connected: true,
  creative: '8 flows live'
}, {
  name: 'Kive.ai',
  type: 'creative',
  maskUrl: 'https://cdn.magicpatterns.com/uploads/aQQjnR3T9uVBwmUXjZvZwc/Kive.svg',
  connected: true,
  role: 'Brand asset library and visual search',
  library: '4,280 assets indexed',
  capabilities: ['Indexes brand and product photography so Carli can pull on-brand references', 'Visual search by look, colorway, and product type', 'Respects the per-upload imagery permissions set in Settings']
}, {
  name: 'Higgsfield.ai',
  type: 'creative',
  iconPath: siYoutube.path,
  connected: false,
  role: 'Generative video and motion',
  library: 'Not connected',
  capabilities: ['Turns approved stills into short-form motion for Reels, Stories, and in-stream', 'Generates 9:16 and 16:9 cuts from a single hero concept', 'Used only when a set is set to Video mode in Ad Studio']
}, {
  name: 'Canva',
  type: 'creative',
  iconPath: siPinterest.path,
  connected: false,
  role: 'Template and layout system',
  library: 'Not connected',
  capabilities: ['Syncs brand templates so generated creative lands inside approved layouts', 'Round-trip editing for anything the team wants to finish by hand']
}];
const campaigns: Campaign[] = [{
  name: 'Spring Collection — Jackets',
  brand: 'Eddy',
  platform: 'Google Ads',
  window: 'Jun 2 – Jul 14',
  spend: '$1,840',
  revenue: '$9,420',
  roas: '5.1x',
  units: '96',
  status: 'Active'
}, {
  name: 'Cardigan search intent',
  brand: 'Eddy',
  platform: 'Google Ads',
  window: 'May 18 – Jun 30',
  spend: '$1,260',
  revenue: '$5,180',
  roas: '4.1x',
  units: '61',
  status: 'Active'
}, {
  name: 'Recovery edit — shopping',
  brand: 'Eddy',
  platform: 'Google Ads',
  window: 'Apr 6 – May 12',
  spend: '$1,180',
  revenue: '$4,020',
  roas: '3.4x',
  units: '48',
  status: 'Completed'
}, {
  name: 'Everyday essentials',
  brand: '1 People',
  platform: 'Google Ads',
  window: 'Jun 10 – Jul 20',
  spend: '$960',
  revenue: '$3,410',
  roas: '3.6x',
  units: '39',
  status: 'Active'
}, {
  name: 'Summer occasion edit',
  brand: 'Eddy',
  platform: 'Facebook Ads',
  window: 'Jul 1 – Aug 18',
  spend: '$2,140',
  revenue: '$8,260',
  roas: '3.9x',
  units: '88',
  status: 'Active'
}, {
  name: 'Coordinated sets test',
  brand: 'Eddy',
  platform: 'Facebook Ads',
  window: 'Aug 1 – Sep 5',
  spend: '$1,120',
  revenue: '—',
  roas: '—',
  units: '—',
  status: 'Scheduled'
}, {
  name: 'Second-life storytelling',
  brand: 'Eddy',
  platform: 'Instagram Ads',
  window: 'Jun 15 – Jul 30',
  spend: '$1,860',
  revenue: '$7,140',
  roas: '3.8x',
  units: '74',
  status: 'Active'
}, {
  name: 'Plus-size spotlight',
  brand: '1 People',
  platform: 'Instagram Ads',
  window: 'May 4 – Jun 8',
  spend: '$740',
  revenue: '$2,180',
  roas: '2.9x',
  units: '24',
  status: 'Completed'
}, {
  name: 'Manymoons partnership push',
  brand: 'Eddy',
  platform: 'Instagram',
  window: 'Monthly',
  spend: '—',
  revenue: '$3,960',
  roas: '—',
  units: '41',
  status: 'Active'
}, {
  name: 'Behind the collection',
  brand: 'Nature Baby',
  platform: 'Facebook',
  window: 'Jun 20 – Jul 20',
  spend: '—',
  revenue: '$1,240',
  roas: '—',
  units: '15',
  status: 'Active'
}, {
  name: 'Recovery drop announcement',
  brand: 'Eddy',
  platform: 'Klaviyo',
  window: 'Jun 4',
  spend: '$180',
  revenue: '$6,480',
  roas: '36x',
  units: '72',
  status: 'Completed'
}, {
  name: 'Abandoned browse flow',
  brand: 'Eddy',
  platform: 'Klaviyo',
  window: 'Always on',
  spend: '$240',
  revenue: '$8,910',
  roas: '37x',
  units: '104',
  status: 'Active'
}, {
  name: 'Winback — lapsed buyers',
  brand: '1 People',
  platform: 'Klaviyo',
  window: 'Always on',
  spend: '$160',
  revenue: '$3,240',
  roas: '20x',
  units: '38',
  status: 'Active'
}];
export function DemandPlatformConnections() {
  const [platforms, setPlatforms] = useState(initialPlatforms);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [brand, setBrand] = useState('All brands');
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandQuery, setBrandQuery] = useState('');
  const [adsOpen, setAdsOpen] = useState(true);
  const [socialOpen, setSocialOpen] = useState(true);
  const [lifecycleOpen, setLifecycleOpen] = useState(true);
  const [creativeOpen, setCreativeOpen] = useState(true);
  const ads = platforms.filter(platform => platform.type === 'ads');
  const social = platforms.filter(platform => platform.type === 'social');
  const lifecycle = platforms.filter(platform => platform.type === 'lifecycle');
  const creativeTools = platforms.filter(platform => platform.type === 'creative');
  const filteredBrands = useMemo(() => owners.filter(owner => owner.toLocaleLowerCase().includes(brandQuery.trim().toLocaleLowerCase())), [brandQuery]);
  const platformCampaigns = useMemo(() => campaigns.filter(campaign => campaign.platform === selectedPlatform?.name && (brand === 'All brands' || campaign.brand === brand)), [selectedPlatform, brand]);
  const toggleConnection = (platform: Platform) => {
    setPlatforms(current => current.map(item => item.name === platform.name && item.type === platform.type ? {
      ...item,
      connected: !item.connected
    } : item));
    setSelectedPlatform(current => current ? {
      ...current,
      connected: !current.connected
    } : null);
  };
  return <section className="mt-6" aria-label="Campaign and social platforms" data-id="element-1062" data-mp="3jjco73saqv6w">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1063" data-mp="3emckv0mu06jh">
        <div data-id="element-1064" data-mp="2t31axlmrml23">
          <h2 className="text-lg font-semibold" data-id="element-1065" data-mp="2t39ntdiw1qda">Channel connections</h2>
          <p className="mt-1 text-xs text-ink/55" data-id="element-1066" data-mp="2y12wqebksyh9">Select a channel to review the campaigns running against your inventory.</p>
        </div>
        <div className="relative w-full sm:w-56" data-id="element-1067" data-mp="32zmd7z7ehi28">
          <button type="button" onClick={() => {
          setBrandOpen(open => !open);
          setBrandQuery('');
        }} aria-expanded={brandOpen} aria-controls="platform-brand-options" className="flex w-full items-center justify-between gap-2 rounded-xl border border-ink/15 bg-paper px-3 py-2.5 text-left text-sm font-semibold text-ink transition-colors hover:border-[#BB5E40]/35 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-1068" data-mp="371r8f2dhymqz">
            <span className="truncate" data-id="element-1069" data-mp="3urll9jxtgbb8">{brand}</span>
            <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform ${brandOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1070" data-mp="3uswq7n2zzp3x" />
          </button>
          {brandOpen && <div id="platform-brand-options" role="listbox" aria-label="Filter campaigns by brand" className="absolute right-0 z-20 mt-2 w-full overflow-hidden rounded-xl border border-ink/10 bg-paper p-1.5 shadow-lg" data-id="element-1071" data-mp="1o44f2fdcysr6">
              <div className="relative mb-1.5" data-id="element-1072" data-mp="1f2wu0yzurof1">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-1073" data-mp="1a43dexp9rhan" />
                <label htmlFor="platform-brand-search" className="sr-only" data-id="element-1074" data-mp="26t19cj8r9l9e">Search brands</label>
                <input id="platform-brand-search" type="search" value={brandQuery} onChange={event => setBrandQuery(event.target.value)} placeholder="Search brands" autoFocus className="w-full rounded-lg border border-ink/10 bg-cream/55 py-2 pl-8 pr-3 text-sm text-ink outline-none placeholder:text-ink/40 focus:border-[#BB5E40]/35" data-id="element-1075" data-mp="22sr458sseyvl" />
              </div>
              <div className="max-h-48 overflow-y-auto" data-id="element-1076" data-mp="2lozykxn8ljvv">
                <button type="button" role="option" aria-selected={brand === 'All brands'} onClick={() => {
              setBrand('All brands');
              setBrandQuery('');
              setBrandOpen(false);
            }} className={`flex w-full rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${brand === 'All brands' ? 'bg-[#BB5E40] text-cream' : 'text-ink/70 hover:bg-cream hover:text-ink'}`} data-id="element-1077" data-mp="2gpwt5c3chbnd">
                  All brands
                </button>
                {filteredBrands.map(item => <button key={item} type="button" role="option" aria-selected={brand === item} onClick={() => {
              setBrand(item);
              setBrandQuery('');
              setBrandOpen(false);
            }} className={`flex w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${brand === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/70 hover:bg-cream hover:text-ink'}`} data-id="element-1078" data-mp="39f3v6l20cjdd">
                    {item}
                  </button>)}
                {filteredBrands.length === 0 && brandQuery.trim() && <p className="px-3 py-4 text-center text-sm text-ink/50" data-id="element-1079" data-mp="3j9reipvz0log">No brands found.</p>}
              </div>
            </div>}
        </div>
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-2" data-id="element-1080" data-mp="39cdchvasnzfo">
        <PlatformSection title="Ads" icon={MegaphoneIcon} description="Paid media channels for campaign spend, creative, and performance." platforms={ads} open={adsOpen} selectedName={selectedPlatform?.name} onToggle={() => {
        setAdsOpen(open => !open);
        setSelectedPlatform(null);
      }} onSelect={setSelectedPlatform} data-id="element-1081" data-mp="399sa956l3hhe" />
        <PlatformSection title="Social" icon={MessagesSquareIcon} description="Publishing and content channels for organic reach and engagement." platforms={social} open={socialOpen} selectedName={selectedPlatform?.name} onToggle={() => {
        setSocialOpen(open => !open);
        setSelectedPlatform(null);
      }} onSelect={setSelectedPlatform} data-id="element-1082" data-mp="1au1jhxr23rmd" />
        <PlatformSection title="Email & SMS" icon={MailIcon} description="Lifecycle channels for owned audience campaigns and automated flows." platforms={lifecycle} open={lifecycleOpen} selectedName={selectedPlatform?.name} onToggle={() => {
        setLifecycleOpen(open => !open);
        setSelectedPlatform(null);
      }} onSelect={setSelectedPlatform} data-id="element-1083" data-mp="x3fzu6ts3dnf" />
        <PlatformSection title="Creative tools" icon={PaletteIcon} description="Asset libraries and generation tools Carli draws on when building creative in Ad Studio." platforms={creativeTools} open={creativeOpen} selectedName={selectedPlatform?.name} onToggle={() => {
        setCreativeOpen(open => !open);
        setSelectedPlatform(null);
      }} onSelect={setSelectedPlatform} data-id="element-1084" data-mp="1kqedkwqik7hn" />
      </div>

      {selectedPlatform && <PlatformDetail platform={selectedPlatform} brand={brand} campaigns={platformCampaigns} onClose={() => setSelectedPlatform(null)} onToggle={() => toggleConnection(selectedPlatform)} data-id="element-1085" data-mp="2fzhv4u8e96km" />}
    </section>;
}
function PlatformSection({
  title,
  icon: Icon,
  description,
  platforms,
  open,
  selectedName,
  onToggle,
  onSelect
}: {
  title: string;
  icon: typeof MegaphoneIcon;
  description: string;
  platforms: Platform[];
  open: boolean;
  selectedName?: string;
  onToggle: () => void;
  onSelect: (platform: Platform) => void;
}) {
  const connected = platforms.filter(platform => platform.connected).length;
  return <article className={`rounded-3xl border border-ink/10 bg-paper shadow-sm ${open ? 'p-5 sm:p-6' : 'px-5 py-4 sm:px-6'}`} data-id="element-1086" data-mp="3cr1zehmlhu2n">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-3 text-left" data-id="element-1087" data-mp="1eebpwbxe63mm"><div className="flex items-center gap-2" data-id="element-1088" data-mp="n2ur0g3e6jbr"><Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1089" data-mp="n1t2b8tgdybk" /><h3 className="text-lg font-semibold" data-id="element-1090" data-mp="j2byskxl7b91">{title}</h3></div><span className="flex items-center gap-2 text-xs font-semibold text-forest" data-id="element-1091" data-mp="j3vea9bf5i9r">{connected} connected{open ? <ChevronDownIcon className="h-4 w-4 text-ink/45" data-id="element-1092" data-mp="wzh94x4c2wa3" /> : <ChevronRightIcon className="h-4 w-4 text-ink/45" data-id="element-1093" data-mp="s3iguyfvwu4e" />}</span></button>
      {open && <><p className="mt-2 text-xs leading-relaxed text-ink/55" data-id="element-1094" data-mp="4cd26mlok7vo">{description}</p><div className="mt-5 grid grid-cols-3 gap-2 sm:grid-cols-4" data-id="element-1095" data-mp="3vj7fbitnt2t4">{platforms.map(platform => <PlatformTile key={platform.name} platform={platform} selected={selectedName === platform.name} onClick={() => onSelect(platform)} data-id="element-1096" data-mp="3vh4iofy04bff" />)}</div></>}
    </article>;
}
function PlatformTile({
  platform,
  selected,
  onClick
}: {
  platform: Platform;
  selected: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} aria-label={`Open ${platform.name} ${platform.type} workspace`} className={`relative flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-xl border p-2 text-center transition-colors ${selected ? 'border-[#BB5E40] bg-[#BB5E40]/8' : platform.connected ? 'border-forest/25 bg-forest/[0.035] hover:border-forest/45' : 'border-ink/10 bg-cream/40 hover:border-[#BB5E40]/35 hover:bg-[#BB5E40]/[0.035]'}`} data-id="element-1097" data-mp="3hpg3u4wdb8ur">
      <PlatformMark platform={platform} data-id="element-1098" data-mp="1nbu8o1qumpp0" />
      <span className="max-w-full truncate text-[11px] font-semibold leading-none text-ink/75" data-id="element-1099" data-mp="2b0n5s1j7j3l7">{platform.name}</span>
      <span className={`absolute right-2 top-2 h-1.5 w-1.5 rounded-full ${platform.connected ? 'bg-forest' : 'bg-ink/20'}`} aria-hidden="true" data-id="element-1100" data-mp="2g0htjw7ord21" />
    </button>;
}
function PlatformDetail({
  platform,
  brand,
  campaigns: platformCampaigns,
  onClose,
  onToggle
}: {
  platform: Platform;
  brand: string;
  campaigns: Campaign[];
  onClose: () => void;
  onToggle: () => void;
}) {
  const totalSpend = platformCampaigns.reduce((sum, campaign) => sum + Number(campaign.spend.replace(/[^0-9.]/g, '') || 0), 0);
  const totalRevenue = platformCampaigns.reduce((sum, campaign) => sum + Number(campaign.revenue.replace(/[^0-9.]/g, '') || 0), 0);
  return <article className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" aria-live="polite" data-id="element-1101" data-mp="2sb95ghxv5ke6">
      <div className="flex items-start justify-between gap-4" data-id="element-1102" data-mp="2nheectfxi352">
        <div className="flex items-center gap-3" data-id="element-1103" data-mp="2jhe29fgay416">
          <PlatformMark platform={platform} data-id="element-1104" data-mp="2ej2rn2i6nphw" />
          <div data-id="element-1105" data-mp="2ejvnv9jimwls">
            <h3 className="text-lg font-semibold" data-id="element-1106" data-mp="2ekdtuxvzcp6w">{platform.name}</h3>
            <p className="mt-1 text-xs text-ink/55" data-id="element-1107" data-mp="29iy8f4pw4gnz">
              {platform.type === 'creative' ? platform.role : `${brand} · ${platform.type === 'ads' ? 'Paid campaign workspace' : platform.type === 'lifecycle' ? 'Email and SMS workspace' : 'Social publishing workspace'}`}
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-ink/45 hover:bg-cream hover:text-ink" aria-label="Close platform details" data-id="element-1108" data-mp="2r775uvoon9q"><XIcon className="h-4 w-4" data-id="element-1109" data-mp="1goi9q675ygp7" /></button>
      </div>

      {platform.type === 'creative' ? <div className="mt-5" data-id="element-1110" data-mp="1lnmi4um9kaeh">
          <div className="rounded-2xl border border-[#BB5E40]/25 bg-[#BB5E40]/[0.05] p-4" data-id="element-1111" data-mp="1lmtl4ld3wz9j">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1112" data-mp="1qfwccmuevwmk">
              <CarliStars data-id="element-1113" data-mp="1uisr22gtz8l8" />
              How Carli uses this
            </p>
            <ul className="mt-3 space-y-2" data-id="element-1114" data-mp="xsa3p06bu1su">
              {(platform.capabilities ?? []).map(capability => <li key={capability} className="flex gap-2 text-xs leading-relaxed text-ink/70" data-id="element-1115" data-mp="12rwe71urt3xy">
                  <CheckCircle2Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1116" data-mp="16thxp61t8gh4" />
                  {capability}
                </li>)}
            </ul>
          </div>

          <div className="mt-4 flex flex-col gap-3 rounded-2xl bg-cream p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1117" data-mp="2i8wsq4agky9l">
            <div data-id="element-1118" data-mp="1ysjxvadvk2ou">
              <p className="text-sm font-semibold text-ink" data-id="element-1119" data-mp="1yu4st143pnn8">
                {platform.connected ? `Connected · ${platform.library}` : `${platform.name} is not connected`}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-ink/60" data-id="element-1120" data-mp="27ln716hhltdo">
                {platform.connected ? 'Carli selects this tool automatically when the brief calls for it. You never pick a model.' : `Connect ${platform.name} to make it available to Carli when generating creative.`}
              </p>
            </div>
            <button type="button" onClick={onToggle} className={`shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold transition-colors ${platform.connected ? 'border border-ink/15 text-ink/70 hover:bg-paper' : 'bg-[#BB5E40] text-cream hover:bg-[#94452E]'}`} data-id="element-1121" data-mp="2cmuef4fpwqjt">
              {platform.connected ? 'Disconnect' : `Connect ${platform.name}`}
            </button>
          </div>
        </div> : platform.connected ? <>
          <div className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-1122" data-mp="m102i9lbo6tr">
            <DetailMetric icon={BarChart3Icon} label={platform.type === 'social' ? 'Campaigns running' : 'Spend'} value={platform.type === 'social' ? `${platformCampaigns.length}` : formatCurrency(totalSpend)} data-id="element-1123" data-mp="8b4vvi9y9wj7" />
            <DetailMetric icon={CheckCircle2Icon} label="Attributed revenue" value={formatCurrency(totalRevenue)} data-id="element-1124" data-mp="3uhlbhqdyezwv" />
            <DetailMetric icon={ImageIcon} label={platform.type === 'lifecycle' ? 'Automations' : 'Creative'} value={platform.creative ?? 'No assets yet'} data-id="element-1125" data-mp="3cuzx5geirwj" />
          </div>

          <div className="mt-5" data-id="element-1126" data-mp="3qfztjqmkbvft">
            <h4 className="text-sm font-semibold" data-id="element-1127" data-mp="3qhayvuvnpb91">{platform.name} campaigns · {brand}</h4>
            {platformCampaigns.length > 0 ? <div className="mt-3 -mx-2 overflow-x-auto px-2" data-id="element-1128" data-mp="1jo7gcof6768n">
                <table className="w-full min-w-[640px] text-left text-xs" data-id="element-1129" data-mp="1oomq7608tvlv">
                  <thead className="border-b border-ink/10 text-ink/50" data-id="element-1130" data-mp="2s919lbqj24xs">
                    <tr data-id="element-1131" data-mp="2wzrhowqc8lw6">
                      <th scope="col" className="pb-2 font-semibold" data-id="element-1132" data-mp="2x0b3ioj3zufh">Campaign</th>
                      <th scope="col" className="pb-2 font-semibold" data-id="element-1133" data-mp="2ibn7kh5cpip0">Brand</th>
                      <th scope="col" className="pb-2 font-semibold" data-id="element-1134" data-mp="2na7y1ouswo09">Window</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1135" data-mp="2n859zad7l4y6">Spend</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1136" data-mp="3b0lqhghasovd">Revenue</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1137" data-mp="3fydkqh5f0n2q">ROAS</th>
                      <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1138" data-mp="31ycioydez9lo">Units</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink/10" data-id="element-1139" data-mp="362kax5j2z7on">
                    {platformCampaigns.map(campaign => <tr key={`${campaign.platform}-${campaign.name}`} data-id="element-1140" data-mp="1um2bcwk6soxs">
                        <th scope="row" className="py-3 pr-3 text-left font-medium text-ink" data-id="element-1141" data-mp="1uj7tncsk5i82">
                          <span className="flex items-center gap-2" data-id="element-1142" data-mp="1n3upi79vv57c">
                            {campaign.name}
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${campaign.status === 'Active' ? 'bg-forest/10 text-forest' : campaign.status === 'Scheduled' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-ink/7 text-ink/55'}`} data-id="element-1143" data-mp="1x07fov67z0xc">{campaign.status}</span>
                          </span>
                        </th>
                        <td className="py-3 pr-3 text-ink/70" data-id="element-1144" data-mp="1e1d3nty54ekv">{campaign.brand}</td>
                        <td className="py-3 pr-3 whitespace-nowrap text-ink/60" data-id="element-1145" data-mp="1e36bhckhp4ug">{campaign.window}</td>
                        <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1146" data-mp="198sc6z2pidki">{campaign.spend}</td>
                        <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1147" data-mp="lipn8ru39nq4">{campaign.revenue}</td>
                        <td className="py-3 text-right tabular-nums text-forest" data-id="element-1148" data-mp="16aczyv0gjfu7">{campaign.roas}</td>
                        <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1149" data-mp="1abghrn22fly2">{campaign.units}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <p className="mt-3 rounded-xl bg-cream/60 px-4 py-6 text-center text-sm text-ink/55" data-id="element-1150" data-mp="1k06p7efvk24g">
                No {platform.name} campaigns for {brand} yet.
              </p>}
          </div>
        </> : <div className="mt-5 flex flex-col gap-3 rounded-2xl bg-cream p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1151" data-mp="rex1mdsugxs0">
          <p className="text-sm leading-relaxed text-ink/65" data-id="element-1152" data-mp="11b0fui0z0mvb">Connect {platform.name} to bring its {platform.type === 'ads' ? 'campaign spend, creative, and result' : platform.type === 'lifecycle' ? 'email, SMS, and audience' : 'publishing and engagement'} data into Carli for Demand.</p>
          <button type="button" onClick={onToggle} className="shrink-0 rounded-full bg-[#BB5E40] px-4 py-2.5 text-xs font-semibold text-cream hover:bg-[#94452E]" data-id="element-1153" data-mp="2cs07dbfhuxoa">Connect platform</button>
        </div>}
    </article>;
}
function DetailMetric({
  icon: Icon,
  label,
  value
}: {
  icon: typeof BarChart3Icon;
  label: string;
  value: string;
}) {
  return <div className="rounded-xl border border-ink/10 bg-cream/45 p-3" data-id="element-1154" data-mp="3iia81ruvjiq9"><Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1155" data-mp="3ehq7eeb3dwzd" /><p className="mt-3 text-xs text-ink/50" data-id="element-1156" data-mp="3ej1dwlvx9iu6">{label}</p><p className="mt-1 text-sm font-semibold" data-id="element-1157" data-mp="39i3xoeuh2yvb">{value}</p></div>;
}
function PlatformMark({
  platform
}: {
  platform: Platform;
}) {
  return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] p-2 text-cream" data-id="element-1158" data-mp="34k2eltx1qzju">
      {platform.maskUrl ? <span aria-hidden="true" className="h-full w-full" style={{
      backgroundColor: '#F7F4EF',
      WebkitMaskImage: `url(${platform.maskUrl})`,
      maskImage: `url(${platform.maskUrl})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-1159" data-mp="12s73pgim6z2o" /> : platform.iconUrl ? <img src={platform.iconUrl} alt="" className="h-full w-full object-contain" data-id="element-1160" data-mp="10tw94vrnxtqk" /> : <svg viewBox="0 0 24 24" aria-hidden="true" className="h-full w-full fill-current" data-id="element-1161" data-mp="n30wr9hygui8">
          <path d={platform.iconPath} data-id="element-1162" data-mp="ru99ofihuy05" />
        </svg>}
    </span>;
}
function formatCurrency(value: number) {
  return value > 0 ? value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }) : '—';
}